import { addDays, parseIsoDate } from './dates';
import type { Contract, ContractId, ContractKind, SupplierId } from './types';

/** Agreement type as shown on screen: acronyms spelled out in plain words. */
export const CONTRACT_KIND_LABEL: Record<ContractKind, string> = {
  MSA: 'Master agreement',
  SOW: 'Statement of work',
  'Supply agreement': 'Supply agreement',
  'Quality agreement': 'Quality agreement',
  'Enterprise agreement': 'Enterprise agreement',
  'Catalog pricing agreement': 'Catalog pricing agreement',
  NDA: 'Confidentiality agreement',
};

export function contractKindLabel(kind: ContractKind): string {
  return CONTRACT_KIND_LABEL[kind];
}

const NO_AUTO_RENEW = { renews: false, termMonths: null, noticeDays: null };

function documentUrl(id: ContractId): string {
  return `/demo/procurement/contracts/${id}.pdf`;
}

/**
 * Contract metadata. Dates are absolute because contracts are static
 * documents. Clauses live in clauses.ts and full text in contract-documents.ts.
 */
export const CONTRACTS: Record<ContractId, Contract> = {
  'MSA-GEN-2025-001': {
    id: 'MSA-GEN-2025-001',
    title: 'Master subscription and services agreement',
    kind: 'MSA',
    supplierId: 'sup-gently',
    type: 'indirect',
    categoryId: 'cat-it-software',
    status: 'active',
    effectiveDate: '2025-11-01',
    endDate: '2027-10-31',
    autoRenew: { renews: true, termMonths: 12, noticeDays: 60 },
    annualValue: 360_000,
    totalValue: 900_000,
    paymentTerms: 'Net 45',
    ownerId: 'daniel-reyes',
    parentId: null,
    exhibits: [
      'Exhibit A — Statement of work 1: accounts payable automation and procurement agents rollout',
      'Exhibit B — Service level agreement',
      'Exhibit C — Data processing agreement',
    ],
    documentUrl: documentUrl('MSA-GEN-2025-001'),
    pages: 16,
    signedBy: {
      buyer: 'Sam Whitfield, Vice President, Finance',
      supplier: 'Authorized signatory, Chief Executive Officer',
    },
    tags: [
      'Software subscription',
      'AI',
      'Service levels',
      'Data protection',
      'SOC 2',
    ],
    summary:
      'Two-year subscription to the Gently accounts payable automation and procurement agents platform at $360,000 per year, plus a $180,000 fixed-fee implementation under statement of work 1. Includes a 99.9% uptime commitment with fee credits when it is missed, data protection terms and a 5% cap on renewal price increases.',
  },
  'SA-CORV-2024-017': {
    id: 'SA-CORV-2024-017',
    title: 'Supply agreement: copper building wire',
    kind: 'Supply agreement',
    supplierId: 'sup-corvane',
    type: 'direct',
    categoryId: 'cat-wire',
    status: 'in_renewal',
    effectiveDate: '2024-01-01',
    endDate: '2026-12-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 31_000_000,
    totalValue: 93_000_000,
    paymentTerms: 'Net 45',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: [
      'Exhibit A — Product specification (UL 83 standard, factory test reports)',
      'Exhibit B — Pricing and copper market price adjustment',
      'Exhibit C — Quality agreement QA-CORV-2024',
    ],
    documentUrl: documentUrl('SA-CORV-2024-017'),
    pages: 8,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Robert Haines, President',
    },
    tags: [
      'Copper building wire',
      'Delivered, freight included',
      'Tied to copper market price',
      'Renewal due',
    ],
    summary:
      'Three-year supply of copper building wire (1/0 gauge) to the Dallas DC and Phoenix DC, priced at $412.00 per 100 ft through 2025 and adjusted annually from 2026 with the copper market price, capped at 3%. Expires December 31, 2026 with no auto-renewal, so a renewal decision is due.',
  },
  'QA-VITX-2023-004': {
    id: 'QA-VITX-2023-004',
    title: 'Quality agreement: 800 A circuit breakers',
    kind: 'Quality agreement',
    supplierId: 'sup-vitrex',
    type: 'direct',
    categoryId: 'cat-circuit',
    status: 'active',
    effectiveDate: '2023-07-01',
    endDate: '2027-06-30',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 0,
    totalValue: 0,
    paymentTerms: 'N/A',
    ownerId: 'priya-raman',
    parentId: 'SA-VITX-2023-004',
    exhibits: [
      'Exhibit A — Specifications and test report requirements',
      'Exhibit B — Change notice matrix',
    ],
    documentUrl: documentUrl('QA-VITX-2023-004'),
    pages: 6,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Dr. Lukas Brenner, Chief Operating Officer',
    },
    tags: [
      'Quality',
      'Change notice',
      'Corrective action',
      'UL-certified factories',
    ],
    summary:
      'Quality terms for 800 A circuit breakers supplied under SA-VITX-2023-004, including 180 days of notice and Pillar approval for any factory or subcontractor change, and shipment only from factories that UL has certified. Gives Pillar the right to reject affected lots and recover costs.',
  },
  'SA-VITX-2023-004': {
    id: 'SA-VITX-2023-004',
    title: 'Supply agreement: 800 A circuit breakers',
    kind: 'Supply agreement',
    supplierId: 'sup-vitrex',
    type: 'direct',
    categoryId: 'cat-circuit',
    status: 'active',
    effectiveDate: '2023-07-01',
    endDate: '2027-06-30',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 11_000_000,
    totalValue: 44_000_000,
    paymentTerms: 'Net 60',
    ownerId: 'marcus-hale',
    parentId: null,
    exhibits: [
      'Exhibit A — Product specifications',
      'Exhibit B — Pricing and forecast',
      'Exhibit C — Quality agreement (QA-VITX-2023-004)',
    ],
    documentUrl: documentUrl('SA-VITX-2023-004'),
    pages: 2,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Dr. Lukas Brenner, Chief Operating Officer',
    },
    tags: ['Circuit breakers', 'UL 489', 'Quality agreement'],
    summary:
      'Four-year supply of 800 A circuit breakers to the Dallas control panel shop and DC at $3,850 each on Net 60 terms. Expires June 30, 2027 and is paired with quality agreement QA-VITX-2023-004.',
  },
  'SA-LUMN-2025-002': {
    id: 'SA-LUMN-2025-002',
    title: 'Supply agreement: motor drives (50 HP)',
    kind: 'Supply agreement',
    supplierId: 'sup-lumen',
    type: 'direct',
    categoryId: 'cat-drives',
    status: 'active',
    effectiveDate: '2025-07-01',
    endDate: '2027-06-30',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 18_700_000,
    totalValue: 37_400_000,
    paymentTerms: 'Net 60',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: [
      'Exhibit A — Product specification (50 HP, 480 V, UL listed)',
      'Exhibit B — Safety stock and logistics',
    ],
    documentUrl: documentUrl('SA-LUMN-2025-002'),
    pages: 6,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Chen Liwei, Chief Executive Officer',
    },
    tags: [
      'Motor drives',
      'Duties and tariffs paid by supplier',
      'Single source',
      'Tariff',
    ],
    summary:
      'Two-year supply of motor drives (50 HP, 480 V) to the Hayward DC at $5,500 each, delivered with duties and tariffs paid by the supplier. The supplier bears all duties and tariffs and holds a 90-day reserve of stock.',
  },
  'SA-AURE-2024-009': {
    id: 'SA-AURE-2024-009',
    title: 'Supply agreement: Pillar Pro LED high-bay fixtures',
    kind: 'Supply agreement',
    supplierId: 'sup-aurelia',
    type: 'direct',
    categoryId: 'cat-lighting',
    status: 'active',
    effectiveDate: '2024-04-01',
    endDate: '2027-03-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 22_000_000,
    totalValue: 66_000_000,
    paymentTerms: 'Net 45, 2% off in 10 days',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: [
      'Exhibit A — Product specifications and light-output data',
      'Exhibit B — Pricing and early-payment discount',
      'Exhibit C — Packaging sustainability roadmap',
    ],
    documentUrl: documentUrl('SA-AURE-2024-009'),
    pages: 6,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Sofía Garza, Director General',
    },
    tags: [
      'LED high-bay',
      'Early-pay discount',
      'Sustainability',
      'RFP-2026-014',
    ],
    summary:
      'Three-year supply of Pillar Pro LED lighting fixtures, including the 150 W high-bay fixture at $88.70 each, on Net 45 with 2% off if paid within 10 days. Expires March 31, 2027 and is being re-bid in RFP-2026-014.',
  },
  'MSA-HALB-2023-011': {
    id: 'MSA-HALB-2023-011',
    title: 'Consulting master services agreement',
    kind: 'MSA',
    supplierId: 'sup-halbrook',
    type: 'indirect',
    categoryId: 'cat-prof-services',
    status: 'active',
    effectiveDate: '2023-09-01',
    endDate: '2028-08-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 3_800_000,
    totalValue: 19_000_000,
    paymentTerms: 'Net 60',
    ownerId: 'daniel-reyes',
    parentId: null,
    exhibits: [
      'Exhibit A — Standard hourly rates',
      'Exhibit B — Expense policy',
    ],
    documentUrl: documentUrl('MSA-HALB-2023-011'),
    pages: 5,
    signedBy: {
      buyer: 'Daniel Reyes, Director, Indirect Procurement',
      supplier: 'Thomas Ellery, Managing Partner',
    },
    tags: ['Consulting', 'Agreed hourly rates', 'Expenses cap'],
    summary:
      'Five-year master agreement for advisory work, with rates and fees set per statement of work. Expenses are capped at 10% of fees and require receipts.',
  },
  'SOW-HALB-2026-03': {
    id: 'SOW-HALB-2026-03',
    title: 'Statement of work: Tariff and network resilience study',
    kind: 'SOW',
    supplierId: 'sup-halbrook',
    type: 'indirect',
    categoryId: 'cat-prof-services',
    status: 'active',
    effectiveDate: '2026-05-01',
    endDate: '2026-11-30',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 450_000,
    totalValue: 450_000,
    paymentTerms: 'Net 60',
    ownerId: 'marcus-hale',
    parentId: 'MSA-HALB-2023-011',
    exhibits: [
      'Exhibit A — Hourly rates',
      'Exhibit B — Deliverables and milestones',
    ],
    documentUrl: documentUrl('SOW-HALB-2026-03'),
    pages: 3,
    signedBy: {
      buyer: 'Daniel Reyes, Director, Indirect Procurement',
      supplier: 'Thomas Ellery, Managing Partner',
    },
    tags: ['Time and materials', 'Budget cap', 'Tariffs', 'RFP-2026-019'],
    summary:
      'Study of tariff exposure and supply network resilience, billed on time and materials from May 1 to November 30, 2026, with a $450,000 budget cap. Billing is limited to four roles at the agreed hourly rates and any other role needs a written change order.',
  },
  'MSA-TRAN-2025-006': {
    id: 'MSA-TRAN-2025-006',
    title: 'Transportation services master agreement',
    kind: 'MSA',
    supplierId: 'sup-transcon',
    type: 'indirect',
    categoryId: 'cat-logistics',
    status: 'active',
    effectiveDate: '2025-04-01',
    endDate: '2028-03-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 21_000_000,
    totalValue: 63_000_000,
    paymentTerms: 'Net 45',
    ownerId: 'daniel-reyes',
    parentId: null,
    exhibits: [
      'Exhibit A — Rates by route',
      'Exhibit B — Fuel surcharge table',
      'Exhibit C — Extra charges schedule',
    ],
    documentUrl: documentUrl('MSA-TRAN-2025-006'),
    pages: 5,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Gregory Dunn, President',
    },
    tags: ['Freight', 'Fuel surcharge', 'Extra charges'],
    summary:
      'Three-year agreement for full-truckload and part-truckload freight covering rates by route, a fuel surcharge capped at 18% of the base freight charge (tied to the U.S. Department of Energy diesel price), and extra charges such as waiting time. Extra charges are payable only with signed proof.',
  },
  'EA-STRT-2024-001': {
    id: 'EA-STRT-2024-001',
    title: 'Enterprise cloud agreement',
    kind: 'Enterprise agreement',
    supplierId: 'sup-stratus',
    type: 'indirect',
    categoryId: 'cat-it-software',
    status: 'expiring',
    effectiveDate: '2024-02-01',
    endDate: '2027-01-31',
    autoRenew: { renews: true, termMonths: 36, noticeDays: 90 },
    annualValue: 12_000_000,
    totalValue: 36_000_000,
    paymentTerms: 'Net 45',
    ownerId: 'kevin-oneill',
    parentId: null,
    exhibits: [
      'Exhibit A — Committed services and agreed rates',
      'Exhibit B — Service level agreement',
      'Exhibit C — Data processing addendum',
    ],
    documentUrl: documentUrl('EA-STRT-2024-001'),
    pages: 5,
    signedBy: {
      buyer: 'Sam Whitfield, Vice President, Finance',
      supplier: 'Rebecca Lindqvist, Vice President, Enterprise Sales',
    },
    tags: ['Cloud', 'Committed spend', 'Auto-renewal', 'Uptime 99.95%'],
    summary:
      'Three-year enterprise cloud agreement with a $12M annual spend commitment, currently tracking 18% under commitment. Auto-renews for 36 months unless notice is given 90 days before January 31, 2027, so the notice deadline is November 2, 2026.',
  },
  'MSA-BRTL-2025-004': {
    id: 'MSA-BRTL-2025-004',
    title: 'Creative and media services master agreement',
    kind: 'MSA',
    supplierId: 'sup-brightline',
    type: 'indirect',
    categoryId: 'cat-marketing',
    status: 'active',
    effectiveDate: '2025-03-01',
    endDate: '2028-02-28',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 6_500_000,
    totalValue: 19_500_000,
    paymentTerms: 'Net 45',
    ownerId: 'lauren-brooks',
    parentId: null,
    exhibits: [
      'Exhibit A — Scope and deliverables',
      'Exhibit B — Milestone billing schedule',
    ],
    documentUrl: documentUrl('MSA-BRTL-2025-004'),
    pages: 2,
    signedBy: {
      buyer: 'Daniel Reyes, Director, Indirect Procurement',
      supplier: 'Nina Alvarez, Chief Executive Officer',
    },
    tags: ['Marketing', 'Milestone billing', 'Pillar Pro'],
    summary:
      'Three-year creative and media services agreement for B2B campaigns, billed by accepted milestone on Net 45 terms. Work is scoped in statements of work approved by the Pillar Pro marketing team.',
  },
  'CPA-NSTR-2025-002': {
    id: 'CPA-NSTR-2025-002',
    title: 'Catalog pricing agreement: IT hardware',
    kind: 'Catalog pricing agreement',
    supplierId: 'sup-northstar',
    type: 'indirect',
    categoryId: 'cat-it-hardware',
    status: 'active',
    effectiveDate: '2025-02-01',
    endDate: '2028-01-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 4_000_000,
    totalValue: 8_000_000,
    paymentTerms: 'Net 30',
    ownerId: 'kevin-oneill',
    parentId: null,
    exhibits: ['Exhibit A — Catalog price list'],
    documentUrl: documentUrl('CPA-NSTR-2025-002'),
    pages: 2,
    signedBy: {
      buyer: 'Daniel Reyes, Director, Indirect Procurement',
      supplier: 'Michael Tran, Vice President, Enterprise Accounts',
    },
    tags: ['Catalog', 'Laptops', 'Fixed pricing'],
    summary:
      'Two-year catalog pricing for business laptops and peripherals, including the 14-inch business laptop at $1,289. Net 30 terms with catalog prices fixed for the term.',
  },
  'SA-HOLL-2024-021': {
    id: 'SA-HOLL-2024-021',
    title: 'Supply agreement: copper building wire and 800 A circuit breakers',
    kind: 'Supply agreement',
    supplierId: 'sup-hollis',
    type: 'direct',
    categoryId: 'cat-wire',
    status: 'active',
    effectiveDate: '2024-07-01',
    endDate: '2027-06-30',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 30_000_000,
    totalValue: 90_000_000,
    paymentTerms: 'Net 45',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: [
      'Exhibit A — Product specifications',
      'Exhibit B — Pricing',
      'Exhibit C — Quality agreement',
    ],
    documentUrl: documentUrl('SA-HOLL-2024-021'),
    pages: 2,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Ellen Whitcombe, Chief Commercial Officer',
    },
    tags: ['Copper building wire', 'Backup breaker supplier', 'Fixed price'],
    summary:
      'Three-year supply of copper building wire (1/0 gauge) at $437.00 per 100 ft on Net 45 terms, also covering 800 A circuit breakers as a qualified backup supplier. Capacity above current volumes is reserved for Pillar.',
  },
  'SA-KAIS-2025-003': {
    id: 'SA-KAIS-2025-003',
    title: 'Supply agreement: THHN copper building wire',
    kind: 'Supply agreement',
    supplierId: 'sup-kaisei',
    type: 'direct',
    categoryId: 'cat-wire',
    status: 'active',
    effectiveDate: '2025-04-01',
    endDate: '2028-03-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 12_000_000,
    totalValue: 36_000_000,
    paymentTerms: 'Net 60',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: [
      'Exhibit A — Product specification',
      'Exhibit B — Pricing',
      'Exhibit C — Logistics and delivery terms',
    ],
    documentUrl: documentUrl('SA-KAIS-2025-003'),
    pages: 2,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Hiroshi Tanabe, Representative Director',
    },
    tags: ['Copper building wire', 'Pillar collects in Osaka', 'Yen exposure'],
    summary:
      'Three-year supply of copper building wire (1/0 gauge) at $448.00 per 100 ft, collected by Pillar in Osaka (Pillar pays freight), on Net 60 terms. Pillar also pays duty, for a delivered cost near $462 per 100 ft.',
  },
  'SA-NGTE-2024-012': {
    id: 'SA-NGTE-2024-012',
    title: 'Supply agreement: LED high-bay fixtures',
    kind: 'Supply agreement',
    supplierId: 'sup-northgate',
    type: 'direct',
    categoryId: 'cat-lighting',
    status: 'active',
    effectiveDate: '2024-06-01',
    endDate: '2027-05-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 9_500_000,
    totalValue: 28_500_000,
    paymentTerms: 'Net 30',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: [
      'Exhibit A — Specifications',
      'Exhibit B — Pricing and volume rebate',
    ],
    documentUrl: documentUrl('SA-NGTE-2024-012'),
    pages: 2,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Gary Lindholm, President',
    },
    tags: ['LED high-bay', 'Volume rebate', 'Domestic supply'],
    summary:
      'Three-year supply of LED high-bay fixtures at $89.95 each on Net 30 terms. Includes a 1.5% volume rebate on all purchases, retroactive once annual purchases pass $8M.',
  },
  'SA-POLR-2022-008': {
    id: 'SA-POLR-2022-008',
    title: 'Supply agreement: electrical enclosures and panel components',
    kind: 'Supply agreement',
    supplierId: 'sup-polaris',
    type: 'direct',
    categoryId: 'cat-enclosures',
    status: 'active',
    effectiveDate: '2022-05-01',
    endDate: '2027-04-30',
    autoRenew: { renews: true, termMonths: 12, noticeDays: null },
    annualValue: 16_000_000,
    totalValue: 112_000_000,
    paymentTerms: 'Net 45',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: ['Exhibit A — Part list and tooling', 'Exhibit B — Pricing'],
    documentUrl: documentUrl('SA-POLR-2022-008'),
    pages: 2,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Dennis Okafor, President',
    },
    tags: ['Renews yearly', 'Single source', 'Tooling'],
    summary:
      'Supply agreement for weatherproof (NEMA 4X) and indoor (NEMA 12) enclosures, mounting sub-panels and wireways for the control panel shop, renewing every 12 months. Pillar owns the tooling, and Net 45 terms apply.',
  },
  'SA-MERI-2025-001': {
    id: 'SA-MERI-2025-001',
    title: 'Supply agreement: PVC conduit and fittings',
    kind: 'Supply agreement',
    supplierId: 'sup-meridian',
    type: 'direct',
    categoryId: 'cat-conduit',
    status: 'active',
    effectiveDate: '2025-01-01',
    endDate: '2027-12-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 14_000_000,
    totalValue: 42_000_000,
    paymentTerms: 'Net 45',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: [
      'Exhibit A — Product specifications (UL 651)',
      'Exhibit B — Pricing tied to the resin price index',
    ],
    documentUrl: documentUrl('SA-MERI-2025-001'),
    pages: 2,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Carla Benton, Vice President, Sales',
    },
    tags: ['PVC conduit', 'Resin-index pricing', 'Schedule 40'],
    summary:
      'Three-year supply of Schedule 40 PVC conduit and fittings to the Dallas DC, priced on a formula tied to the PVC resin market price. Net 45 terms with annual 12-month renewals after the initial term.',
  },
  'SA-CLRW-2024-006': {
    id: 'SA-CLRW-2024-006',
    title: 'Supply agreement: wiring devices',
    kind: 'Supply agreement',
    supplierId: 'sup-clearwell',
    type: 'direct',
    categoryId: 'cat-devices',
    status: 'active',
    effectiveDate: '2024-09-01',
    endDate: '2027-08-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 5_000_000,
    totalValue: 15_000_000,
    paymentTerms: 'Net 45',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: ['Exhibit A — Product specifications', 'Exhibit B — Pricing'],
    documentUrl: documentUrl('SA-CLRW-2024-006'),
    pages: 2,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Anil Mehra, President',
    },
    tags: ['Receptacles', 'Switches', 'UL 498'],
    summary:
      'Three-year supply of commercial and industrial receptacles, switches and dimmers on Net 45 terms. Every shipment is UL listed product with date-coded packaging and a certificate of conformance.',
  },
  'SA-STLR-2024-005': {
    id: 'SA-STLR-2024-005',
    title: 'Supply agreement: dry-type transformers',
    kind: 'Supply agreement',
    supplierId: 'sup-stellar',
    type: 'direct',
    categoryId: 'cat-power',
    status: 'active',
    effectiveDate: '2024-03-01',
    endDate: '2027-02-28',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 7_200_000,
    totalValue: 21_600_000,
    paymentTerms: 'Net 45',
    ownerId: 'marcus-hale',
    parentId: null,
    exhibits: [
      'Exhibit A — Product schedule and lead times',
      'Exhibit B — Efficiency and test requirements',
    ],
    documentUrl: documentUrl('SA-STLR-2024-005'),
    pages: 2,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Patricia Lindgren, Chief Executive Officer',
    },
    tags: ['Transformers', 'Energy efficiency rule', 'Lead time'],
    summary:
      'Three-year supply of dry-type distribution and control power transformers on Net 45 terms. The product schedule and lead times are in Exhibit A, and the federal energy efficiency and test requirements are in Exhibit B.',
  },
  'MSA-KEYS-2024-010': {
    id: 'MSA-KEYS-2024-010',
    title: 'Contingent labor master services agreement',
    kind: 'MSA',
    supplierId: 'sup-keystone',
    type: 'indirect',
    categoryId: 'cat-contingent',
    status: 'active',
    effectiveDate: '2024-05-01',
    endDate: '2027-04-30',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 8_500_000,
    totalValue: 34_000_000,
    paymentTerms: 'Net 45',
    ownerId: 'daniel-reyes',
    parentId: null,
    exhibits: [
      'Exhibit A — Rate and markup schedule',
      'Exhibit B — Overtime and approval policy',
    ],
    documentUrl: documentUrl('MSA-KEYS-2024-010'),
    pages: 2,
    signedBy: {
      buyer: 'Daniel Reyes, Director, Indirect Procurement',
      supplier: 'Brian Kowalski, President',
    },
    tags: ['Staffing', 'Markup cap', 'Overtime approval'],
    summary:
      'Three-year contingent labor agreement for warehouse, panel shop and administrative roles. Supplier markup is capped at 32% and overtime requires prior written approval.',
  },
  'MSA-CLVW-2024-003': {
    id: 'MSA-CLVW-2024-003',
    title: 'Facility services master agreement',
    kind: 'MSA',
    supplierId: 'sup-clearview',
    type: 'indirect',
    categoryId: 'cat-facilities',
    status: 'active',
    effectiveDate: '2024-01-01',
    endDate: '2026-12-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 5_400_000,
    totalValue: 16_200_000,
    paymentTerms: 'Net 45',
    ownerId: 'daniel-reyes',
    parentId: null,
    exhibits: ['Exhibit A — Scope of services', 'Exhibit B — Service levels'],
    documentUrl: documentUrl('MSA-CLVW-2024-003'),
    pages: 2,
    signedBy: {
      buyer: 'Daniel Reyes, Director, Indirect Procurement',
      supplier: 'Anthony Russo, President',
    },
    tags: ['Facilities', 'Janitorial', 'Floor care'],
    summary:
      'Three-year facility services agreement covering janitorial, grounds and warehouse floor care at the Dallas DC and headquarters, billed on Net 45 terms. Service levels and inspection scores are set in Exhibit B.',
  },
  'NDA-KEST-2026-001': {
    id: 'NDA-KEST-2026-001',
    title: 'Mutual non-disclosure agreement',
    kind: 'NDA',
    supplierId: 'sup-kestrel',
    type: 'direct',
    categoryId: 'cat-drives',
    status: 'active',
    effectiveDate: '2026-02-12',
    endDate: '2028-02-11',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 0,
    totalValue: 0,
    paymentTerms: 'N/A',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: [],
    documentUrl: documentUrl('NDA-KEST-2026-001'),
    pages: 2,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Mikkel Sørensen, Chief Executive Officer',
    },
    tags: ['Confidentiality', 'Mutual', 'Onboarding'],
    summary:
      'Signed mutual confidentiality agreement covering the evaluation and qualification of Kestrel Drives as a second supplier for 50 HP motor drives. The parties may exchange confidential information for two years, and obligations survive for five years after that period.',
  },
  'SA-MROS-2025-004': {
    id: 'SA-MROS-2025-004',
    title: 'Supply agreement: maintenance and industrial supplies',
    kind: 'Supply agreement',
    supplierId: 'sup-mro-supply',
    type: 'direct',
    categoryId: 'cat-mro',
    status: 'active',
    effectiveDate: '2025-02-01',
    endDate: '2028-01-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 9_500_000,
    totalValue: 28_500_000,
    paymentTerms: '2% in 10 days, else Net 30',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: [],
    documentUrl: documentUrl('SA-MROS-2025-004'),
    pages: 3,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Dale Whitaker, President',
    },
    tags: [
      'Maintenance supplies',
      'Strut channel',
      'Early-pay discount',
      'Freight paid by supplier',
    ],
    summary:
      'Three-year supply of hydraulic fittings, strut channel, brackets and valves at fixed unit prices, with 2% off if paid within 10 days and otherwise due in 30. Orders of $5,000 or more ship with freight paid by the supplier, and invoices may come from the parent billing entity, Apex Industrial Group.',
  },
  'SA-BRST-2024-011': {
    id: 'SA-BRST-2024-011',
    title: 'Supply agreement: electrical conduit, boxes and fittings',
    kind: 'Supply agreement',
    supplierId: 'sup-border-states',
    type: 'direct',
    categoryId: 'cat-conduit',
    status: 'active',
    effectiveDate: '2024-08-01',
    endDate: '2027-07-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 7_500_000,
    totalValue: 22_500_000,
    paymentTerms: 'Net 45',
    ownerId: 'sarah-chen',
    parentId: null,
    exhibits: [],
    documentUrl: documentUrl('SA-BRST-2024-011'),
    pages: 3,
    signedBy: {
      buyer: 'Marcus Hale, Vice President, Supply Chain',
      supplier: 'Curtis Maddox, Vice President, Sales',
    },
    tags: [
      'Fill-in supply',
      'Backorder coverage',
      'Steel conduit',
      'Freight included',
    ],
    summary:
      'Three-year fill-in and backorder supply of steel conduit, junction boxes and liquidtight fittings at fixed prices, delivered with freight included, on Net 45 terms. Pillar has no minimum volume, and a shorter term printed on an invoice has no effect.',
  },
  'MSA-BRZV-2025-002': {
    id: 'MSA-BRZV-2025-002',
    title: 'Logistics services master agreement',
    kind: 'MSA',
    supplierId: 'sup-brazos',
    type: 'indirect',
    categoryId: 'cat-logistics',
    status: 'active',
    effectiveDate: '2025-06-01',
    endDate: '2028-05-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 2_000_000,
    totalValue: 6_000_000,
    paymentTerms: 'Net 45',
    ownerId: 'daniel-reyes',
    parentId: null,
    exhibits: [],
    documentUrl: documentUrl('MSA-BRZV-2025-002'),
    pages: 3,
    signedBy: {
      buyer: 'Daniel Reyes, Director, Indirect Procurement',
      supplier: 'Harold Voss, President',
    },
    tags: [
      'Warehouse and logistics',
      'Pallet handling',
      'Extra charges',
      'Signed proof',
    ],
    summary:
      'Three-year agreement for pallet handling, ambient storage, shuttle loads and export crating at the Bryan warehouse, on Net 45 terms. Crating is the one allowed extra charge at $600 per job and needs proof signed by a Pillar facility representative.',
  },
  'MSA-SNTL-2025-003': {
    id: 'MSA-SNTL-2025-003',
    title: 'Freight services master agreement',
    kind: 'MSA',
    supplierId: 'sup-sentinel',
    type: 'indirect',
    categoryId: 'cat-logistics',
    status: 'active',
    effectiveDate: '2025-09-01',
    endDate: '2028-08-31',
    autoRenew: NO_AUTO_RENEW,
    annualValue: 1_300_000,
    totalValue: 3_900_000,
    paymentTerms: 'Net 15',
    ownerId: 'daniel-reyes',
    parentId: null,
    exhibits: [],
    documentUrl: documentUrl('MSA-SNTL-2025-003'),
    pages: 3,
    signedBy: {
      buyer: 'Daniel Reyes, Director, Indirect Procurement',
      supplier: 'Gloria Mendez, President',
    },
    tags: ['Part-truckload', 'Fuel surcharge', 'Net 15', 'Proof of delivery'],
    summary:
      'Three-year part-truckload and full-truckload agreement for inbound supplier freight, including a $1,200 flat rate from Houston to the Dallas DC. A fuel surcharge applies only above $4.00 diesel and is capped at 15%, waiting-time charges need signed proof, and payment is Net 15.',
  },
};

const CONTRACT_IDS: string[] = Object.keys(CONTRACTS);

/** Type guard for route params and other untyped input. */
export function isContractId(value: string): value is ContractId {
  return CONTRACT_IDS.includes(value);
}

export function getContract(id: ContractId): Contract {
  return CONTRACTS[id];
}

export const CONTRACT_LIST: Contract[] = Object.values(CONTRACTS);

export function contractsForSupplier(supplierId: SupplierId): Contract[] {
  return CONTRACT_LIST.filter((c) => c.supplierId === supplierId);
}

/** Contracts whose parent is `parentId`, such as SOWs under an MSA. */
export function childContracts(parentId: ContractId): Contract[] {
  return CONTRACT_LIST.filter((c) => c.parentId === parentId);
}

/**
 * Last day to give notice of non-renewal: the end date minus the notice
 * period. Null when the contract does not auto-renew.
 */
export function noticeDeadline(contract: Contract): Date | null {
  const { renews, noticeDays } = contract.autoRenew;
  if (!renews || noticeDays === null) return null;
  return addDays(parseIsoDate(contract.endDate), -noticeDays);
}
